import * as React from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useMediaQuery, useTheme } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useLoginMutation } from "../../services/auth.service";
import { Navigate } from "react-router-dom";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { isManage360, isProductionMode } from "../../utils/helper";
import CornerRibbon from "../../components/CornerRibbon";
import workz360 from "../../images/workz360.png";
import manage360 from "../../images/manage360.png";
import { StyledTextField, CardBase } from "../../components/global";
import { colors } from "@mui/material";

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: 12,
  padding: "12px 24px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#FF6B35",
  color: "#ffffff",
  boxShadow: "0 4px 14px rgba(255, 107, 53, 0.4)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.15)",
    borderRadius: 12,
    pointerEvents: "none",
  },
  "&:hover": {
    backgroundColor: "#E55A2B",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(255, 107, 53, 0.6)",
    "&::before": {
      background: "rgba(255, 255, 255, 0.2)",
    },
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "& .MuiButton-label": {
    position: "relative",
    zIndex: 1,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 20px",
    fontSize: "0.875rem",
  },
}));

function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ opacity: 0.7, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
      {...props}
    >
      {"Copyright © "}
      <Link
        color="inherit"
        href="https://workz360.com/"
        sx={{
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Workz360
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

export default function LoginPage() {
  const [login, { isLoading }] = useLoginMutation();
  const user = useCurrentUser();
  const isProduction = React.useMemo(() => isProductionMode(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data: any) => {
    const { email, password } = data;
    await login({ username: email, password });
  };

  if (!!user) return <Navigate to="/dashboard" replace={true} />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { xs: "100%", sm: "100vh" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: "url('/new-background-home.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", sm: "fixed" },
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 480 },
          position: "relative",
          zIndex: 1,
        }}
      >

        <Box sx={{ position: "relative" }}>
          {!isProduction && (
            <CornerRibbon
              position="top-right"
              backgroundColor={colors.yellow[200]}
              fontColor="#333"
            >
              STAGING
            </CornerRibbon>
          )}
          <CardBase
            useGlassCard={false}
            nested={false}
            sx={{
              p: { xs: 2.5, sm: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
            }}
          >

            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              <Box
                component="img"
                src={isManage360() ? manage360 : workz360}
                alt="Logo"
                sx={{
                  height: { xs: 44, sm: 56 },
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))",
                }}
              />
              <Typography
                component="h1"
                variant="h5"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              >
                Admin Dashboard
              </Typography>
            </Box>

            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              sx={{ width: "100%", mt: { xs: 1.5, sm: 2 } }}
            >
              <Controller
                name="email"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    margin="normal"
                    fullWidth
                    id="email"
                    size={isMobile ? "small" : "medium"}
                    label="Email Address"
                    autoComplete="email"
                    autoFocus
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    inputRef={ref}
                    error={!!errors.email}
                    helperText={!!errors.email && "This field is required"}
                  />
                )}
              />
              <Controller
                name="password"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    margin="normal"
                    fullWidth
                    label="Password"
                    type="password"
                    id="password"
                    size={isMobile ? "small" : "medium"}
                    autoComplete="current-password"
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    inputRef={ref}
                    error={!!errors.password}
                    helperText={!!errors.password && "This field is required"}
                  />
                )}
              />
              <StyledLoadingButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 } }}
                loading={isLoading}
              >
                Sign In
              </StyledLoadingButton>
              <Copyright sx={{ mt: { xs: 3, sm: 4 } }} />
            </Box>
          </CardBase>
        </Box>
      </Box>
    </Box>
  );
}
